// Phase 2 — Admin Email Campaigns & Templates
// =================================================
// Campaign builder + template library with Send Test to Admin, approval
// workflow, bulk send to eligible subscribers, and a per-campaign delivery
// log. Lives at /admin/email-marketing/campaigns and /admin/email-marketing/templates.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  CaretLeft, EnvelopeSimple, Plus, Trash, PencilSimple, PaperPlaneTilt,
  CheckCircle, Warning, Clock, Download, Eye, FileText, ArrowClockwise,
  Users,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { AgentLayout } from "@/components/AgentLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

const inputCls =
  "bg-black/40 border-white/15 text-white placeholder:text-white/30 focus:border-[#D4AF37] font-sans";

const STATUS_CHIP = {
  draft:     "bg-white/10 text-white/70 border-white/15",
  approved:  "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  scheduled: "bg-sky-500/15 text-sky-200 border-sky-500/30",
  sending:   "bg-amber-500/15 text-amber-200 border-amber-500/30 animate-pulse",
  sent:      "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30",
  failed:    "bg-rose-500/15 text-rose-200 border-rose-500/30",
  cancelled: "bg-white/10 text-white/40 border-white/10",
};

// ---------------------------------------------------------------------------
// Sub-nav strip (shared across the three email-marketing pages)
// ---------------------------------------------------------------------------
function EmailSubNav({ active }) {
  const items = [
    { k: "contacts",  label: "Contacts",  to: "/admin/email-marketing" },
    { k: "campaigns", label: "Campaigns", to: "/admin/email-marketing/campaigns" },
    { k: "templates", label: "Templates", to: "/admin/email-marketing/templates" },
  ];
  return (
    <div className="mt-5 inline-flex items-center gap-1 rounded-lg border border-white/10 bg-black/40 p-1" data-testid="email-sub-nav">
      {items.map((it) => (
        it.k === active ? (
          <span key={it.k} className="px-3 py-1.5 rounded-md bg-[#D4AF37] text-black text-[10px] uppercase tracking-widest font-sans">
            {it.label}
          </span>
        ) : (
          <Link key={it.k} to={it.to} data-testid={`sub-nav-${it.k}`}
                className="px-3 py-1.5 rounded-md text-white/60 hover:text-[#D4AF37] text-[10px] uppercase tracking-widest font-sans transition-colors">
            {it.label}
          </Link>
        )
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Templates page
// ---------------------------------------------------------------------------
function TemplateDialog({ open, onOpenChange, template, onSaved }) {
  const isEdit = !!template?.id;
  const [form, setForm] = useState({
    name: "", category: "general", subject_default: "",
    preview_text_default: "", html_body: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (template) {
      setForm({
        name: template.name || "",
        category: template.category || "general",
        subject_default: template.subject_default || "",
        preview_text_default: template.preview_text_default || "",
        html_body: template.html_body || "",
      });
    } else if (open) {
      setForm({ name: "", category: "general", subject_default: "", preview_text_default: "", html_body: "" });
    }
  }, [template, open]);

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/email-marketing/templates/${template.id}`, form);
      } else {
        await api.post("/admin/email-marketing/templates", form);
      }
      toast.success(isEdit ? "Template updated" : "Template created");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-black border border-white/10 max-h-[85vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-serif text-2xl">
            {isEdit ? "Edit template" : "New template"}
          </DialogTitle>
          <DialogDescription className="text-white/50 text-xs">
            Reusable branded template. Use <code className="text-[#D4AF37]">{"{{first_name}}"}</code> as a merge token.
            The unsubscribe link + mailing address footer is injected automatically on every send.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="sm:col-span-2">
            <label className="text-[9px] uppercase tracking-widest text-white/40">Name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="template-name-input" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-white/40">Category</label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className={inputCls} data-testid="template-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="cruise">Cruise</SelectItem>
                <SelectItem value="packages">Vacation packages</SelectItem>
                <SelectItem value="promotions">Promotions</SelectItem>
                <SelectItem value="offers">Special offers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-white/40">Default subject</label>
            <Input value={form.subject_default} onChange={(e) => setForm({ ...form, subject_default: e.target.value })} className={inputCls} data-testid="template-subject-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[9px] uppercase tracking-widest text-white/40">Preview text</label>
            <Input value={form.preview_text_default} onChange={(e) => setForm({ ...form, preview_text_default: e.target.value })} className={inputCls} data-testid="template-preview-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[9px] uppercase tracking-widest text-white/40">HTML body</label>
            <Textarea value={form.html_body} onChange={(e) => setForm({ ...form, html_body: e.target.value })} className={`${inputCls} min-h-[260px] font-mono text-xs`} data-testid="template-body-input"
              placeholder="<h1>Dream Caribbean Cruise</h1><p>Hi {{first_name}}, our 7-night Caribbean cruise starts at $999...</p>" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/15 bg-transparent text-white/70 hover:bg-white/5" data-testid="template-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving || !form.name || !form.html_body} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest" data-testid="template-save-btn">
            {saving ? "Saving…" : "Save template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-marketing/templates");
      setTemplates(data || []);
    } catch (e) {
      toast.error("Could not load templates");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/email-marketing/templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <AgentLayout>
      <div className="mb-6 animate-fade-up" data-testid="admin-templates-page">
        <Link to="/admin" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.25em] text-white/40 hover:text-[#D4AF37] transition-colors font-sans mb-4">
          <CaretLeft size={12} weight="bold" /> Back to dashboard
        </Link>
        <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3 font-sans flex items-center gap-2">
          <FileText size={12} weight="duotone" /> Email templates
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-white mb-3">
          Branded <span className="italic text-[#D4AF37]">templates</span>
        </h1>
        <p className="text-white/60 text-sm max-w-2xl font-sans">
          Reusable HTML templates for cruises, packages, promotions and offers.
          Templates power every campaign and automatically carry the mailing
          address + unsubscribe footer.
        </p>
        <EmailSubNav active="templates" />
      </div>

      <div className="flex justify-end mb-4">
        <Button onClick={() => { setActive(null); setDialogOpen(true); }} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest" data-testid="new-template-btn">
          <Plus size={14} className="mr-2" /> New template
        </Button>
      </div>

      <div className="rounded-xl border border-white/10 bg-black/40 overflow-hidden">
        <table className="w-full text-sm font-sans" data-testid="template-table">
          <thead>
            <tr className="text-[9px] uppercase tracking-widest text-white/40 border-b border-white/10">
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Updated</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="px-4 py-8 text-center text-white/40 text-xs">Loading…</td></tr>}
            {!loading && templates.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-white/40 text-xs">
                No templates yet. Create one — it becomes available in every campaign.
              </td></tr>
            )}
            {!loading && templates.map((t, i) => (
              <tr key={t.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`template-row-${i}`}>
                <td className="px-4 py-3 text-white/90">{t.name}</td>
                <td className="px-4 py-3 text-white/60 text-xs capitalize">{t.category}</td>
                <td className="px-4 py-3 text-white/50 text-xs">{new Date(t.updated_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setActive(t); setDialogOpen(true); }} className="h-8 w-8 p-0 text-white/50 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10" title="Edit" data-testid={`template-edit-${i}`}>
                    <PencilSimple size={14} weight="duotone" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(t)} className="h-8 w-8 p-0 text-white/50 hover:text-rose-400 hover:bg-rose-500/10" title="Delete" data-testid={`template-delete-${i}`}>
                    <Trash size={14} weight="duotone" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TemplateDialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setActive(null); }} template={active} onSaved={load} />
    </AgentLayout>
  );
}

// ---------------------------------------------------------------------------
// Campaign builder dialog
// ---------------------------------------------------------------------------
function CampaignDialog({ open, onOpenChange, campaign, templates, onSaved, adminEmail }) {
  const isEdit = !!campaign?.id;
  const [form, setForm] = useState({
    name: "", subject: "", preview_text: "", template_id: "", html_body: "",
    audience: { include_statuses: ["subscribed"], sources: [], tags: [], advisor_ids: [], since_date: "" },
    scheduled_for: "",
  });
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState(adminEmail || "");
  const [sendingTest, setSendingTest] = useState(false);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [approving, setApproving] = useState(false);
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    if (campaign) {
      setForm({
        name: campaign.name || "",
        subject: campaign.subject || "",
        preview_text: campaign.preview_text || "",
        template_id: campaign.template_id || "",
        html_body: campaign.html_body || "",
        audience: campaign.audience || { include_statuses: ["subscribed"], sources: [], tags: [], advisor_ids: [], since_date: "" },
        scheduled_for: campaign.scheduled_for || "",
      });
    } else if (open) {
      setForm({
        name: "", subject: "", preview_text: "", template_id: "", html_body: "",
        audience: { include_statuses: ["subscribed"], sources: [], tags: [], advisor_ids: [], since_date: "" },
        scheduled_for: "",
      });
    }
    setTestEmail(adminEmail || "");
    setAudiencePreview(null);
  }, [campaign, open, adminEmail]);

  // Auto-fill subject/body from template on template change
  const pickTemplate = (tid) => {
    setForm((f) => {
      const tpl = (templates || []).find((t) => t.id === tid);
      return {
        ...f,
        template_id: tid,
        subject: f.subject || (tpl?.subject_default || ""),
        preview_text: f.preview_text || (tpl?.preview_text_default || ""),
        html_body: f.html_body || (tpl?.html_body || ""),
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        await api.patch(`/admin/email-marketing/campaigns/${campaign.id}`, form);
        saved = { id: campaign.id };
      } else {
        const { data } = await api.post("/admin/email-marketing/campaigns", form);
        saved = data;
      }
      toast.success(isEdit ? "Campaign saved" : "Draft created");
      onSaved?.(saved);
      return saved;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const previewAudience = async () => {
    const saved = isEdit ? { id: campaign.id } : (await save());
    if (!saved) return;
    try {
      const { data } = await api.get(`/admin/email-marketing/campaigns/${saved.id}/audience`);
      setAudiencePreview(data);
      toast.success(`${data.total_eligible} eligible subscribers`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audience lookup failed");
    }
  };

  const sendTest = async () => {
    const saved = isEdit ? { id: campaign.id } : (await save());
    if (!saved) return;
    setSendingTest(true);
    try {
      const { data } = await api.post(`/admin/email-marketing/campaigns/${saved.id}/test`, { to_email: testEmail });
      if (data.ok) toast.success(`Test sent to ${testEmail}`);
      else toast.error(`Test send failed: ${data.error || data.status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally {
      setSendingTest(false);
    }
  };

  const approve = async () => {
    const saved = isEdit ? { id: campaign.id } : (await save());
    if (!saved) return;
    setApproving(true);
    try {
      await api.post(`/admin/email-marketing/campaigns/${saved.id}/approve`);
      toast.success("Campaign approved — ready to send");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally {
      setApproving(false);
    }
  };

  const launch = async () => {
    if (!campaign?.id || campaign.status !== "approved") {
      toast.error("Approve the campaign first.");
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Send this campaign to every eligible subscriber?\nThis cannot be undone.`)) return;
    setLaunching(true);
    try {
      const { data } = await api.post(`/admin/email-marketing/campaigns/${campaign.id}/send`);
      toast.success(`Queued ${data.queued} recipients. Delivery runs in the background.`);
      onSaved?.(campaign);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setLaunching(false);
    }
  };

  const isLocked = isEdit && ["sending", "sent"].includes(campaign?.status);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-black border border-white/10 max-h-[88vh] overflow-y-auto" data-testid="campaign-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-serif text-2xl">
            {isEdit ? `Edit campaign — ${campaign.name}` : "New campaign"}
          </DialogTitle>
          <DialogDescription className="text-white/50 text-xs">
            Draft → send a test to yourself → approve → send to subscribers.
            Sent campaigns automatically exclude unsubscribed and cleaned contacts.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="sm:col-span-2">
            <label className="text-[9px] uppercase tracking-widest text-white/40">Campaign name (internal)</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} disabled={isLocked} data-testid="campaign-name-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[9px] uppercase tracking-widest text-white/40">Subject line</label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} disabled={isLocked} data-testid="campaign-subject-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[9px] uppercase tracking-widest text-white/40">Preview text</label>
            <Input value={form.preview_text} onChange={(e) => setForm({ ...form, preview_text: e.target.value })} className={inputCls} disabled={isLocked} data-testid="campaign-preview-input" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-white/40">Base template</label>
            <Select value={form.template_id || "__none__"} onValueChange={(v) => pickTemplate(v === "__none__" ? "" : v)} disabled={isLocked}>
              <SelectTrigger className={inputCls} data-testid="campaign-template-select"><SelectValue placeholder="Choose template" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Start from scratch —</SelectItem>
                {(templates || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-white/40">Schedule (optional)</label>
            <Input type="datetime-local" value={form.scheduled_for} onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })} className={inputCls} disabled={isLocked} data-testid="campaign-schedule-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[9px] uppercase tracking-widest text-white/40">Email body (HTML)</label>
            <Textarea value={form.html_body} onChange={(e) => setForm({ ...form, html_body: e.target.value })} className={`${inputCls} min-h-[260px] font-mono text-xs`} disabled={isLocked} data-testid="campaign-body-input" />
          </div>
          <div className="sm:col-span-2 rounded-md border border-white/10 bg-black/40 p-3">
            <div className="text-[9px] uppercase tracking-widest text-white/40 mb-2">Audience</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <label className="text-[9px] uppercase tracking-widest text-white/40">Statuses</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {["subscribed", "pending"].map((s) => {
                    const on = (form.audience.include_statuses || []).includes(s);
                    return (
                      <button key={s} type="button" onClick={() => setForm({ ...form, audience: {
                        ...form.audience,
                        include_statuses: on
                          ? form.audience.include_statuses.filter((x) => x !== s)
                          : [...(form.audience.include_statuses || []), s],
                      }})}
                        className={`px-2 py-1 rounded border text-[9px] uppercase tracking-widest ${on ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60 hover:text-white"}`}
                        data-testid={`audience-status-${s}`}>
                        {s}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[9px] text-white/40 mt-2">Unsubscribed &amp; cleaned contacts are always excluded.</p>
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-widest text-white/40">Tags (comma)</label>
                <Input value={(form.audience.tags || []).join(", ")} onChange={(e) => setForm({ ...form, audience: { ...form.audience, tags: e.target.value.split(",").map((s)=>s.trim()).filter(Boolean) }})} className={inputCls} data-testid="audience-tags-input" placeholder="VIP, Honeymoon" />
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-widest text-white/40">Signed up since</label>
                <Input type="date" value={form.audience.since_date} onChange={(e) => setForm({ ...form, audience: { ...form.audience, since_date: e.target.value }})} className={inputCls} data-testid="audience-since-input" />
              </div>
            </div>
            <div className="mt-3 flex gap-2 items-center">
              <Button type="button" size="sm" variant="outline" onClick={previewAudience} className="border-white/15 bg-transparent text-white/80 hover:bg-white/5 text-xs" data-testid="audience-preview-btn">
                <Users size={12} className="mr-1.5" /> Preview audience
              </Button>
              {audiencePreview && (
                <span className="text-xs text-emerald-300">
                  {audiencePreview.total_eligible} eligible · {audiencePreview.sample.slice(0, 3).map((s) => s.email).join(", ")}{audiencePreview.sample.length > 3 ? "…" : ""}
                </span>
              )}
            </div>
          </div>

          {/* TEST SEND */}
          <div className="sm:col-span-2 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3">
            <div className="text-[9px] uppercase tracking-widest text-[#D4AF37] mb-2 flex items-center gap-1">
              <PaperPlaneTilt size={11} weight="duotone" /> Preview before launch
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="text-[9px] uppercase tracking-widest text-white/40">Send test to</label>
                <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className={inputCls} placeholder="info@besttimetravelandtours.com" data-testid="campaign-test-email-input" />
              </div>
              <Button onClick={sendTest} disabled={sendingTest || !testEmail || !form.subject || !form.html_body} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest" data-testid="campaign-send-test-btn">
                {sendingTest ? "Sending…" : "Send test email to Admin"}
              </Button>
            </div>
            <p className="text-[10px] text-white/50 mt-2">
              The test send uses the real campaign render (including unsubscribe link + mailing-address footer). Nothing is written to the delivery log.
            </p>
          </div>

          {/* STATUS / LAUNCH BAR */}
          {isEdit && (
            <div className="sm:col-span-2 rounded-md border border-white/10 bg-black/40 p-3 flex flex-wrap items-center gap-3 justify-between">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-white/40 uppercase tracking-widest text-[9px]">Status</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[9px] uppercase tracking-widest ${STATUS_CHIP[campaign?.status] || ""}`}>
                  {campaign?.status}
                </span>
                {campaign?.sent_count !== undefined && (campaign.sent_count + campaign.failed_count) > 0 && (
                  <span className="text-white/60 text-xs">
                    · {campaign.sent_count} sent / {campaign.failed_count} failed
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                {campaign?.status === "approved" && (
                  <Button onClick={launch} disabled={launching} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest" data-testid="campaign-launch-btn">
                    <PaperPlaneTilt size={12} className="mr-1.5" /> {launching ? "Launching…" : "Send to subscribers"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/15 bg-transparent text-white/70 hover:bg-white/5" data-testid="campaign-cancel-btn">
            Close
          </Button>
          {!isLocked && (
            <>
              <Button onClick={save} disabled={saving || !form.name} variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/5 text-xs uppercase tracking-widest" data-testid="campaign-save-draft-btn">
                {saving ? "Saving…" : "Save draft"}
              </Button>
              <Button onClick={approve} disabled={approving || !form.subject || !form.html_body} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs uppercase tracking-widest" data-testid="campaign-approve-btn">
                <CheckCircle size={12} className="mr-1.5" /> {approving ? "Approving…" : "Approve for sending"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Campaigns list page
// ---------------------------------------------------------------------------
export function AdminEmailCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [adminEmail, setAdminEmail] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, t, me] = await Promise.all([
        api.get("/admin/email-marketing/campaigns"),
        api.get("/admin/email-marketing/templates"),
        api.get("/auth/me").catch(() => ({ data: {} })),
      ]);
      setCampaigns(c.data || []);
      setTemplates(t.data || []);
      setAdminEmail(me.data?.email || "");
    } catch (e) {
      toast.error("Could not load campaigns");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/email-marketing/campaigns/${c.id}`);
      toast.success("Campaign deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const openCampaign = async (c) => {
    // Fetch latest state (status may have changed in background send loop)
    try {
      const { data } = await api.get(`/admin/email-marketing/campaigns/${c.id}`);
      setActive(data);
    } catch {
      setActive(c);
    }
    setDialogOpen(true);
  };

  return (
    <AgentLayout>
      <div className="mb-6 animate-fade-up" data-testid="admin-campaigns-page">
        <Link to="/admin" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.25em] text-white/40 hover:text-[#D4AF37] transition-colors font-sans mb-4">
          <CaretLeft size={12} weight="bold" /> Back to dashboard
        </Link>
        <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3 font-sans flex items-center gap-2">
          <EnvelopeSimple size={12} weight="duotone" /> Campaigns
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-white mb-3">
          Email <span className="italic text-[#D4AF37]">campaigns</span>
        </h1>
        <p className="text-white/60 text-sm max-w-2xl font-sans">
          Draft, test, approve and send branded campaigns from inside Best Time
          Travel &amp; Tours. Every send excludes unsubscribed and cleaned
          contacts automatically.
        </p>
        <EmailSubNav active="campaigns" />
      </div>

      <div className="flex justify-between items-center mb-4">
        <Button variant="outline" onClick={load} className="border-white/15 bg-transparent text-white/70 hover:bg-white/5 text-xs" data-testid="campaigns-refresh-btn">
          <ArrowClockwise size={12} className="mr-1.5" /> Refresh
        </Button>
        <Button onClick={() => { setActive(null); setDialogOpen(true); }} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest" data-testid="new-campaign-btn">
          <Plus size={14} className="mr-2" /> New campaign
        </Button>
      </div>

      <div className="rounded-xl border border-white/10 bg-black/40 overflow-hidden">
        <table className="w-full text-sm font-sans" data-testid="campaign-table">
          <thead>
            <tr className="text-[9px] uppercase tracking-widest text-white/40 border-b border-white/10">
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Subject</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Sent / Failed</th>
              <th className="text-left px-4 py-3">Updated</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40 text-xs">Loading…</td></tr>}
            {!loading && campaigns.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-white/40 text-xs">
                No campaigns yet. Create your first draft.
              </td></tr>
            )}
            {!loading && campaigns.map((c, i) => (
              <tr key={c.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`campaign-row-${i}`}>
                <td className="px-4 py-3 text-white/90">{c.name}</td>
                <td className="px-4 py-3 text-white/70 text-xs truncate max-w-[260px]">{c.subject || <span className="text-white/30">—</span>}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[9px] uppercase tracking-widest ${STATUS_CHIP[c.status] || ""}`}>
                    {c.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-xs text-white/70">
                  {c.status === "sent" || c.status === "sending" || c.status === "failed" ? (
                    <span>{c.sent_count || 0} / {c.failed_count || 0}</span>
                  ) : <span className="text-white/30">—</span>}
                </td>
                <td className="px-4 py-3 text-white/50 text-xs">{new Date(c.updated_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  <a href={`/api/admin/email-marketing/campaigns/${c.id}/export.csv`} target="_blank" rel="noreferrer"
                     className="inline-flex items-center justify-center h-8 w-8 text-white/50 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded" title="Export recipient CSV" data-testid={`campaign-export-${i}`}>
                    <Download size={14} weight="duotone" />
                  </a>
                  <Button size="sm" variant="ghost" onClick={() => openCampaign(c)} className="h-8 w-8 p-0 text-white/50 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10" title="Open" data-testid={`campaign-edit-${i}`}>
                    <PencilSimple size={14} weight="duotone" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c)} disabled={c.status === "sending"} className="h-8 w-8 p-0 text-white/50 hover:text-rose-400 hover:bg-rose-500/10 disabled:opacity-30" title="Delete" data-testid={`campaign-delete-${i}`}>
                    <Trash size={14} weight="duotone" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CampaignDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setActive(null); }}
        campaign={active}
        templates={templates}
        adminEmail={adminEmail}
        onSaved={() => load()}
      />
    </AgentLayout>
  );
}

export default AdminEmailCampaigns;
